import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DisclosureReceiptPanel from "@/components/DisclosureReceiptPanel";

export const CriticalFindingScenario = ({ scenario, katlasStatus }) => {
    const [open, setOpen] = useState(false);
    const [route, setRoute] = useState("contact_patient");
    const [carer, setCarer] = useState("none");
    const [msg, setMsg] = useState(
        scenario?.patient_facing_message_options?.[0] || "review_requested"
    );
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState(null);

    if (!scenario) return null;

    const submit = async () => {
        setBusy(true);
        try {
            const res = await api
                .post("/healthcierge/disclosure/govern", {
                    actor_role: "cardiovascular_review_clinician",
                    route_choice: route,
                    patient_facing_message_type: msg,
                    carer_involvement: carer,
                })
                .then((r) => r.data);
            setResponse(res);
            if (res.ok) {
                toast.success(
                    "Disclosure route recorded · KATLAS-signed"
                );
            } else {
                toast(
                    `Action recorded · KATLAS ${res.katlas?.status || "unconfigured"}`,
                    {
                        description:
                            "No simulated receipt produced (honest unconfigured state).",
                    }
                );
            }
            setTimeout(() => {
                document
                    .getElementById("disclosure-receipt-anchor")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 80);
        } catch (e) {
            console.error(e);
            toast.error("Disclosure action failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="panel" data-testid="critical-finding-scenario">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full px-6 sm:px-8 py-5 flex items-center justify-between text-left hover:bg-[var(--paper-alt)] transition-colors"
                data-testid="critical-scenario-toggle"
                aria-expanded={open}
            >
                <div>
                    <p className="mono-label">Optional scenario · secondary</p>
                    <h2
                        className="font-serif mt-1"
                        style={{ fontSize: 24, letterSpacing: "-0.01em" }}
                    >
                        {scenario.title}
                    </h2>
                </div>
                <div className="flex items-center gap-3 mono-label">
                    {open ? "Collapse" : "Expand"}
                    {open ? (
                        <ChevronUp size={16} strokeWidth={1.5} />
                    ) : (
                        <ChevronDown size={16} strokeWidth={1.5} />
                    )}
                </div>
            </button>

            {open && (
                <div className="border-t hairline">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        <div className="px-6 sm:px-8 py-6 lg:border-r hairline">
                            <p className="mono-label">Patient-facing screen</p>
                            <div
                                className="mt-3 panel-alt px-4 py-4 border border-[var(--rule)]"
                                data-testid="patient-facing-screen"
                            >
                                <p
                                    className="font-serif"
                                    style={{
                                        fontSize: 18,
                                        lineHeight: 1.4,
                                        color: "var(--ink)",
                                    }}
                                >
                                    “{scenario.patient_facing_message}”
                                </p>
                                <p
                                    className="mono-label mt-3"
                                    style={{ color: "var(--ink-muted)" }}
                                >
                                    No diagnostic label · no raw result · no
                                    finding text
                                </p>
                            </div>
                        </div>
                        <div className="px-6 sm:px-8 py-6">
                            <p className="mono-label">Clinician-facing concern</p>
                            <p
                                className="mt-3"
                                style={{
                                    fontSize: 14.5,
                                    color: "var(--ink-soft)",
                                    lineHeight: 1.6,
                                }}
                            >
                                {scenario.clinician_facing_concern}
                            </p>
                            <p
                                className="mono-label mt-4"
                                style={{ color: "var(--forest)" }}
                            >
                                Signal category · {scenario.signal_category}
                            </p>
                            <p
                                className="mono-label"
                                style={{ color: "var(--forest)" }}
                            >
                                Disclosure lock applied · true
                            </p>
                        </div>
                    </div>

                    <div className="px-6 sm:px-8 py-6 border-t hairline">
                        <p className="mono-label mb-3">Clinician next step</p>
                        <div className="flex flex-wrap gap-2">
                            {(scenario.clinician_choices || []).map((c) => {
                                const active = route === c.key;
                                return (
                                    <button
                                        key={c.key}
                                        onClick={() => setRoute(c.key)}
                                        className="px-3 py-2 border transition-colors"
                                        data-testid={`route-choice-${c.key}`}
                                        style={{
                                            background: active
                                                ? "var(--ink)"
                                                : "transparent",
                                            color: active
                                                ? "#fff"
                                                : "var(--ink)",
                                            borderColor: "var(--ink)",
                                            fontSize: 13,
                                        }}
                                    >
                                        {c.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="mono-label mb-2">
                                    Patient-facing message type
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {(
                                        scenario.patient_facing_message_options ||
                                        []
                                    ).map((opt) => {
                                        const active = msg === opt;
                                        return (
                                            <button
                                                key={opt}
                                                onClick={() => setMsg(opt)}
                                                className="px-3 py-2 border transition-colors mono-data"
                                                data-testid={`msg-option-${opt}`}
                                                style={{
                                                    background: active
                                                        ? "var(--forest)"
                                                        : "transparent",
                                                    color: active
                                                        ? "#fff"
                                                        : "var(--ink)",
                                                    borderColor:
                                                        "var(--forest)",
                                                    fontSize: 12,
                                                }}
                                            >
                                                {opt}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <p className="mono-label mb-2">
                                    Carer involvement
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {(
                                        scenario.carer_involvement_options || []
                                    ).map((opt) => {
                                        const active = carer === opt;
                                        return (
                                            <button
                                                key={opt}
                                                onClick={() => setCarer(opt)}
                                                className="px-3 py-2 border transition-colors mono-data"
                                                data-testid={`carer-option-${opt}`}
                                                style={{
                                                    background: active
                                                        ? "var(--forest)"
                                                        : "transparent",
                                                    color: active
                                                        ? "#fff"
                                                        : "var(--ink)",
                                                    borderColor:
                                                        "var(--forest)",
                                                    fontSize: 12,
                                                }}
                                            >
                                                {opt}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <button
                            data-testid="disclosure-govern-btn"
                            className="btn-accent mt-6"
                            disabled={busy}
                            onClick={submit}
                        >
                            {busy
                                ? "Submitting…"
                                : "Record governed disclosure route"}
                        </button>

                        {katlasStatus && katlasStatus !== "active" && (
                            <p
                                className="mt-3 mono-label"
                                style={{ color: "#B8860B" }}
                            >
                                KATLAS {katlasStatus} · honest unconfigured
                                response will be returned
                            </p>
                        )}
                    </div>

                    <div id="disclosure-receipt-anchor">
                        {response && (
                            <div className="px-6 sm:px-8 py-6 border-t hairline">
                                <DisclosureReceiptPanel response={response} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default CriticalFindingScenario;
